// SEO <head> builders for the public blog routes. Isomorphic and client-safe:
// they read only plain loaderData (title/description/canonical) — never a
// Firebase param — so they run identically on the SSR server and after client
// hydration/navigation. `canonical` is resolved server-side in the query layer.
import { isProduction } from "@/config/config";
import { GOODSENDER_ICON_LINKS } from "@/lib/brandIcons";
import { searchAppearanceFor } from "../../../functions/src/blog/searchAppearance";
import type { Blog } from "../../../functions/src/blog/db";
import type { FaqPageLd } from "../../../functions/src/blog/faq";
import { faviconEntries } from "../../../functions/src/blog/logoLinks";
import type { Post } from "../../../functions/src/posts/db";
import { coverImageAltText, coverImageSrc, safeHttpUrl } from "./format";
import { normalizeTerms, TAXONOMY_LABEL, type TaxonomyKind } from "./taxonomy";

// Off-prod pages must never be indexed (mirrors the #221 SEO-parity pattern).
const ROBOTS = isProduction
  ? "index, follow, max-image-preview:large"
  : "noindex, nofollow";

// Publisher identity + shared social-card image for structured data / OG /
// Twitter. Mirrors the constants the docs (`docs-json-ld.tsx`) and the other
// SEO libs (`faq-seo.ts`, `pricing-seo.ts`, `landing-seo.ts`) already use — the
// blog has no per-post OG image yet, so it shares the branded site card.
const SITE_NAME = "GoodSender";
const OG_IMAGE = "https://goodsender.com/og-image.png";

/**
 * The locale every blog page declares (#923).
 *
 * A deliberate MIRROR of `BLOG_OG_LOCALE` in `functions/src/blog/renderShared.ts`,
 * not an import: that module reads `APP_BASE_URL` off `process.env` at call time
 * and is bundled for the SSR server, while this one is client-safe and runs again
 * after hydration — the same boundary `src/lib/blog/format.ts` mirrors
 * `coverImageSrc` across. Both spellings are pinned to the `lang="en"` these pages
 * already hardcode, so if blog language ever becomes a setting, all three move
 * together.
 */
const OG_LOCALE = "en_US";

// A JSON-LD block. TanStack Router renders a `{ "script:ld+json": <object> }`
// head-meta entry as `<script type="application/ld+json">…</script>` server-side
// (JSON.stringify + HTML-escape are applied by the router), so the structured
// data is present in the exported HTML for Google rich results / answer engines.
type JsonLd = Record<string, unknown>;
type MetaEntry =
  | { title: string }
  | { "script:ld+json": JsonLd }
  | Record<string, string>;
export type RouteHead = {
  meta: MetaEntry[];
  links: Array<Record<string, string>>;
};

// Derive the site origin and blog home from the RSS feed URL
// (`<origin>/blog/rss.xml`) so structured-data URLs stay env-consistent
// (dev vs prod) without reading a Firebase param in this client-safe module.
function siteUrls(feedUrl: string): { origin: string; blogHome: string } {
  const blogHome = feedUrl.replace(/\/rss\.xml$/, ""); // <origin>/blog
  const origin = blogHome.replace(/\/blog$/, ""); // <origin>
  return { origin, blogHome };
}

/**
 * #1041 — the publication logo (#841) is the Organization's `logo` when set
 * (through `coverImageSrc`, the same https-only re-check `faviconLinks` above
 * already applies to this same `blog.logo`), `OG_IMAGE` only when there is
 * none. This mirrors `blogHead.ts`'s `publisher()` logo-precedence *order*
 * (logo beats a generic image), not its exact shape: `blogHead.ts` OMITS
 * `logo` entirely rather than substituting a GoodSender-branded stand-in
 * (its own doc: substituting one there is "exactly wrong" for a customer's
 * custom domain) and has a hero-image tier in between that this function has
 * no config to read. `OG_IMAGE` here is strictly the no-logo case, per the
 * issue's fix direction.
 *
 * `name` deliberately stays `SITE_NAME` rather than mirroring `blog.title`.
 * True today for the `/blog/*` routes (`src/routes/blog/*.tsx`), which all
 * hardcode `resolveDogfoodWorkspaceId()`, so `blogIndexHead` never sees a
 * second publisher. NOT true for `blogPostHead` in isolation: it is also
 * called from `/preview/$postId.tsx` for an arbitrary workspace's own draft
 * (`checkPublishPermission` since the Publish-gate retirement admits every
 * workspace), where `name: "GoodSender"` next to that workspace's own logo is
 * a pre-existing mismatch (predates #1041 — the favicon `<link>`s the same
 * page emits already carry the workspace's own logo instead of GoodSender's,
 * per #841). Out of scope here: #1041's acceptance criteria are the `/blog`
 * index and post pages, and the preview surface is noindexed
 * (`X-Robots-Tag: noindex`) so no crawler ever reads this JSON-LD there.
 */
function organization(origin: string, blog: Blog): JsonLd {
  const logo = coverImageSrc(blog.logo?.url) ?? OG_IMAGE;
  return {
    "@type": "Organization",
    name: SITE_NAME,
    url: origin,
    logo: { "@type": "ImageObject", url: logo },
  };
}

function breadcrumbLd(items: Array<{ name: string; item: string }>): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

// Serialize a Date-or-string timestamp to an ISO string. loaderData is
// JSON-serialized across the SSR boundary, so a `Date` on the server can arrive
// as a string after hydration — handle both.
function isoOrUndefined(value: Date | string | null): string | undefined {
  if (value == null) return undefined;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * One options object rather than seven positional parameters, for the reason
 * `metaTags`'s server-side twin took the same shape (D-923-3): the `<link
 * rel="alternate">` feed title and the page title are two same-typed strings
 * that must NOT be the same value, and two adjacent same-typed parameters is
 * precisely the shape that produced the defect this fixes.
 *
 * `blog` rather than a `feedTitle: string` for the same reason one step further
 * on: all four builders would only ever pass `blog.title`, so a `feedTitle` field
 * is a fifth builder's chance to pass the page title again. Handing `head` the
 * blog leaves it no wrong value to be given.
 */
type HeadInput = {
  /**
   * The blog these pages belong to — read for the FEED title alone. `<link
   * rel="alternate" type="application/rss+xml" title>` names the subscription a
   * reader is offered, so a post page that passed its own title made every
   * subscribe-from-a-post read as a feed of that one post (#923).
   */
  blog: Blog;
  /** The page's own title — `<title>`, `og:title`, `twitter:title`. */
  title: string;
  description: string;
  canonical: string;
  feedUrl: string;
  ogType: "website" | "article";
  jsonLd: JsonLd[];
  /**
   * `og:image` / `twitter:image` override — the post's own cover (#1155),
   * already resolved through `coverImageSrc` by the caller. `OG_IMAGE` when
   * omitted, which is every list builder (FR-005) and a post with no https
   * cover (FR-004).
   */
  image?: string;
  /**
   * `og:image:alt` / `twitter:image:alt` (#1269) — the author's trimmed cover
   * alt text. Set only when `image` IS the post's cover; omitted otherwise.
   */
  imageAlt?: string;
};

/**
 * The publication's favicon `<link>` entries (#841) when a logo exists, else
 * `GOODSENDER_ICON_LINKS` (#1099 / US2) — a hosted blog page must always carry
 * exactly one icon set, never zero. The logo-present list is `faviconEntries`
 * (`functions/src/blog/logo.ts`) — the SAME list the server-rendered
 * custom-domain head serializes — so the two renderers of the same data cannot
 * drift; this side only applies the client mirror of `blogHead.ts`'s https
 * re-check (`coverImageSrc`). The hosted `/blog` path never imports
 * `functions/src/blog/render.ts`, so both must emit the favicon independently
 * or the hosted path would silently never get one. `__root.tsx` omits its own
 * icon links on every `/blog*`/`/preview*` match so this is the only source of
 * icon links on those routes.
 *
 * The `manifest` entry `faviconEntries` may produce is dropped here (#1101,
 * US4, assumption A8): the hosted app-host `/blog` path has no server route
 * serving it — it 301s to the custom-domain blog subdomain in dev and prod
 * anyway — so linking it would point a browser at a 404. A future
 * `src/routes/blog/manifest[.]webmanifest.ts` for the dogfood workspace is
 * what would lift this filter (spec US4 S4).
 */
function faviconLinks(blog: Blog): Array<Record<string, string>> {
  if (!blog.logo) return [...GOODSENDER_ICON_LINKS];
  return faviconEntries(blog.logo)
    .filter((entry) => entry.rel !== "manifest")
    .flatMap((entry) => {
      const href = coverImageSrc(entry.href);
      return href ? [{ ...entry, href }] : [];
    });
}

/**
 * `og:image`/`twitter:image` for a list page (landing, taxonomy index, term) —
 * card ?? https hero ?? `OG_IMAGE` (#1156, FR-003). Unlike the post page there
 * is no per-post cover to prefer first, and no `blog.logo` gate either: a list
 * page shows the card whenever one is current, logo or not (spec S4/S5) —
 * `blog.ogCardUrl` is already gated by `mapBlog` (stale/untitled reads as
 * null, never served).
 */
function blogListImage(blog: Blog): string {
  return blog.ogCardUrl ?? coverImageSrc(blog.themeConfig.heroImage) ?? OG_IMAGE;
}

function head({
  blog,
  title,
  description,
  canonical,
  feedUrl,
  ogType,
  jsonLd,
  image,
  imageAlt,
}: HeadInput): RouteHead {
  const resolvedImage = image ?? OG_IMAGE;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: ROBOTS },
      { property: "og:type", content: ogType },
      // On every page kind, list and post alike — these documents are `lang="en"`
      // whatever they list (#923).
      { property: "og:locale", content: OG_LOCALE },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      { property: "og:image", content: resolvedImage },
      ...(imageAlt ? [{ property: "og:image:alt", content: imageAlt }] : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: resolvedImage },
      ...(imageAlt ? [{ name: "twitter:image:alt", content: imageAlt }] : []),
      ...jsonLd.map((ld): MetaEntry => ({ "script:ld+json": ld })),
    ],
    links: [
      { rel: "canonical", href: canonical },
      // Feed autodiscovery — kept from the #199 renderer so readers/extensions
      // can still find /blog/rss.xml from the list and post pages.
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: blog.title,
        href: feedUrl,
      },
      ...faviconLinks(blog),
    ],
  };
}

export function blogIndexHead(
  blog: Blog,
  canonical: string,
  feedUrl: string,
): RouteHead {
  const { origin, blogHome } = siteUrls(feedUrl);
  const appearance = searchAppearanceFor({ kind: "blog", blog });
  const jsonLd: JsonLd[] = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: blog.title,
      description: appearance.description,
      url: canonical,
      publisher: organization(origin, blog),
    },
    breadcrumbLd([
      { name: "Home", item: origin },
      { name: "Blog", item: blogHome },
    ]),
  ];
  return head({
    blog,
    title: appearance.title,
    description: appearance.description,
    canonical,
    feedUrl,
    ogType: "website",
    jsonLd,
    image: blogListImage(blog),
  });
}

// The verb form ("tagged" / "in") drives the term-page description; the display
// label ("Tags"/"Categories") comes from the shared TAXONOMY_LABEL.
const KIND_VERB: Record<TaxonomyKind, string> = {
  tags: "tagged",
  categories: "in",
};

/**
 * <head> for a taxonomy index page (`/blog/tags`, `/blog/categories`) — the list
 * of every tag/category. A `website` OG type with a Home → Blog → <Kind>
 * breadcrumb, mirroring the Hugo taxonomy list pages.
 */
export function blogTaxonomyHead(
  blog: Blog,
  kind: TaxonomyKind,
  canonical: string,
  feedUrl: string,
): RouteHead {
  const { origin, blogHome } = siteUrls(feedUrl);
  const label = TAXONOMY_LABEL[kind];
  const title = `${label} · ${blog.title}`;
  const description = `Browse every ${
    kind === "tags" ? "tag" : "category"
  } on ${blog.title}.`;
  const jsonLd: JsonLd[] = [
    breadcrumbLd([
      { name: "Home", item: origin },
      { name: "Blog", item: blogHome },
      { name: label, item: canonical },
    ]),
  ];
  return head({
    blog,
    title,
    description,
    canonical,
    feedUrl,
    ogType: "website",
    jsonLd,
    image: blogListImage(blog),
  });
}

/**
 * <head> for a single term page (`/blog/tags/<term>`) — the posts carrying one
 * tag/category. `website` OG type with a Home → Blog → <Kind> → <term>
 * breadcrumb (the <Kind> crumb points at the taxonomy index).
 */
export function blogTermHead(
  blog: Blog,
  kind: TaxonomyKind,
  term: string,
  canonical: string,
  feedUrl: string,
): RouteHead {
  const { origin, blogHome } = siteUrls(feedUrl);
  const label = TAXONOMY_LABEL[kind];
  const title = `${term} · ${label} · ${blog.title}`;
  const description = `Posts ${KIND_VERB[kind]} ${term} on ${blog.title}.`;
  const jsonLd: JsonLd[] = [
    breadcrumbLd([
      { name: "Home", item: origin },
      { name: "Blog", item: blogHome },
      { name: label, item: `${blogHome}/${kind}` },
      { name: term, item: canonical },
    ]),
  ];
  return head({
    blog,
    title,
    description,
    canonical,
    feedUrl,
    ogType: "website",
    jsonLd,
    image: blogListImage(blog),
  });
}

export function blogPostHead(
  // The blog, for the FEED title alone — the `<link rel="alternate">` names the
  // subscription, not the page (#923, D-923-4). First, like the three sibling
  // builders above, and typed rather than a bare title string: `Blog` and `Post`
  // cannot be transposed by accident, two adjacent strings can.
  blog: Blog,
  // Neither the body (`markdown`) nor the agent `provenance` is read here, and
  // the loader strips both from the payload — accept the slimmer shape so this
  // signature reflects that. Provenance in particular must never reach a head
  // tag: these are the public SEO surfaces (#608).
  post: Omit<Post, "markdown" | "provenance">,
  canonical: string,
  feedUrl: string,
  /**
   * The `ItemList` node for a list-shaped post (#926), or undefined for every
   * other post — which is most of them, and whose head is byte-unchanged.
   *
   * Passed IN rather than derived here, and that is forced rather than stylistic:
   * deciding it needs the post's markdown through `dominantOrderedList`, and this
   * module is the client-safe head builder that also runs after hydration — it
   * would have to ship both markdown-it and the whole body to do it. The loader
   * has the body in hand server-side and hands over the finished node
   * (`loadBlogPost` in `./queries`), which is also why `BlogPostView` can keep
   * stripping `markdown` from the payload.
   */
  itemList?: JsonLd,
  /** Derived server-side from visible FAQ tokens before markdown leaves the loader. */
  faqPage?: FaqPageLd,
): RouteHead {
  const { origin, blogHome } = siteUrls(feedUrl);
  // The machine line (PLACEMENT: postDescription → meta-only): the stored description
  // when the author wrote one, else derived from the summary — shared with the
  // themed renderer through the import-free search-appearance resolver.
  const appearance = searchAppearanceFor({ kind: "post", post });
  const description = appearance.description;
  const datePublished = isoOrUndefined(post.publishedAt);
  /*
   * The post's own taxonomy, in the graph (#923).
   *
   * The `<meta property="article:tag">` lines the themed renderer emits per tag
   * have no equivalent on this surface: TanStack Router keys its head `meta` by
   * `name ?? property` and drops every repeat (`headContentUtils.js`, both the
   * SSR and the post-hydration path in 1.170.x), so a repeatable OG property is
   * not expressible through the router's head API — five `article:tag` entries
   * would render as one. `keywords`/`articleSection` are the same claim in the
   * channel this page CAN emit, and they are what the themed `BlogPosting`
   * already carries, so the two renderers now describe a post's taxonomy alike.
   *
   * Through `normalizeTerms`, for the reason the themed head gives: the Hugo
   * importer writes `tags` straight from front matter, so a blank entry would be
   * a keyword asserted to exist and to be nameless.
   */
  const tags = normalizeTerms(post.tags ?? []);
  const categories = normalizeTerms(post.categories ?? []);
  // Computed once (#1041 review) — the same `Organization` node fills both the
  // no-author `author` fallback and `publisher` below, and now that it reads
  // `blog.logo` through `coverImageSrc` it's no longer free to build twice.
  const publisher = organization(origin, blog);
  // The post's own cover (#1155), through the same https-only re-check the
  // logo above already goes through.
  const cover = coverImageSrc(post.coverImageUrl);
  // Named author only, like the rendered byline (#1205).
  const authorAvatar = post.author?.trim() ? coverImageSrc(post.authorAvatarUrl) : undefined;
  // `BlogPosting.image` stays cover-only (never the card, spec A6) — `OG_IMAGE`
  // is the pre-#1155 fallback, kept byte-identical when there is no https
  // cover (FR-004).
  const postingImage = cover ?? OG_IMAGE;
  // `og:image`/`twitter:image` (#1156): cover ?? (blog.logo ? card : https
  // hero) ?? OG_IMAGE — mirrors `blogHead.ts`'s custom-domain chain (FR-003),
  // with `OG_IMAGE` as goodsender.com's own last resort (A5) rather than the
  // custom-domain omission. `blog.ogCardUrl` is already gated by `mapBlog`
  // (stale/untitled reads as null, never served).
  const hero = coverImageSrc(blog.themeConfig.heroImage);
  const ogImage =
    cover ?? (blog.logo ? (blog.ogCardUrl ?? undefined) : hero) ?? OG_IMAGE;
  const coverAlt = coverImageAltText(post.coverImageAlt);
  const posting: JsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description,
    ...(datePublished ? { datePublished } : {}),
    dateModified: isoOrUndefined(post.updatedAt) ?? datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    url: canonical,
    image: postingImage,
    author: post.author
      ? {
          "@type": "Person",
          name: post.author,
          ...(safeHttpUrl(post.authorUrl) ? { url: safeHttpUrl(post.authorUrl) } : {}),
          ...(authorAvatar ? { image: authorAvatar } : {}),
        }
      : publisher,
    publisher,
    ...(tags.length ? { keywords: tags.join(", ") } : {}),
    ...(categories.length ? { articleSection: categories } : {}),
  };
  const jsonLd: JsonLd[] = [
    posting,
    // ADDITIONAL to `BlogPosting`, never instead of it — the page is still an
    // article. Ordered after it for the same reason the themed head orders it
    // there: the article node is what every existing result keys on.
    ...(itemList ? [itemList] : []),
    ...(faqPage ? [faqPage] : []),
    breadcrumbLd([
      { name: "Home", item: origin },
      { name: "Blog", item: blogHome },
      { name: post.title, item: canonical },
    ]),
  ];
  return head({
    blog,
    title: appearance.title,
    description,
    canonical,
    feedUrl,
    ogType: "article",
    jsonLd,
    image: ogImage,
    // Alt text describes the cover, so only when the social image IS the cover.
    imageAlt: cover && ogImage === cover && coverAlt ? coverAlt : undefined,
  });
}
